/**
 * The record page, `/record/:ref` (13-25): where a register record is read and worked.
 *
 * - **Which record:** a live ref (`INC-42`; `inc-0042` is canonicalised) is read through `useRecord` (13-23). A
 *   practice ref (`INC-P3`, D-13-23-1) opens the practice record, in practice mode only. Anything else gets a calm
 *   "That reference isn't a register record" and a way to Lookup.
 * - **Read:** the head (serif title, ref, register, status, business unit, lodged date `D MMM YYYY`, "Your stream"
 *   from the C3 map (D-13-L-15, a marker only), "Client id to add" (D-13-L-7), and "Attachments: open on
 *   SharePoint" (the item's form, D-13-L-6)); the columns by stage (`FieldView`); the sibling timeline; the closure
 *   trail (`useClosureTrail`), with open links flagged (D-13-L-14).
 * - **Act:** the panel slots (`panels/registry.ts`) this person's role can use on this record, through 13-22's gate.
 *   Every write goes through the runtime's Writer (`VerbForm` → `commitRecordVerb`); dev and the practice ribbon
 *   write nothing (D-13-L-9).
 * - **Owed audit rows** (D-13-L-5, ISS-263): on opening a live record, the signed-in person's owed rows for it are
 *   retried through the Writer, and any still owed are said, with "Post them now".
 * - **Collapsible** (13.2-11, D-13.2-A-5 / D-13.2-P-11): every panel, every read section, the timeline and the
 *   closure links collapse. They start as `openByDefault` says: everything collapsed (D-13.2-A-9, Andy 26 Oct), with
 *   this person's next step tagged "Your next step" and a `#panel-<key>` deep link open; a person's toggles last for the visit only: no storage, re-set when
 *   the ref changes, never on an eTag refresh (a save doesn't snap sections shut). A `#panel-<key>` link followed on
 *   the page opens that panel and scrolls to it. "Expand all" / "Collapse all" sit beside each area's heading. A panel
 *   that becomes visible after a save and is this person's to act on (the Regulator notices after an NDIS-Reportable
 *   decision, ISS-286) opens once (13.2-10); one they only read stays closed.
 * - **13.2-10 head notes** (D-13.2-P-10, D-13.2-A-1): "Report incomplete: N to add" on an open incident whose report
 *   lacks a field (`missingLodgeColumns`), linking to Complete the report when the person has it; "Duplicate of INC-n"
 *   on a record closed as one, linking to the record kept; and, for Oversight only, "Possible duplicate of INC-n"
 *   with the reasons in words (`useDuplicates`: one bounded, gated read). The signal never acts: closing is the
 *   audited *Close as a duplicate* step, which takes the top match as its prefill (`DuplicatesContext`).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useReducedMotion } from 'motion/react';
import { PageContainer } from '../../components/layout';
import { Badge, Button, Spinner } from '../../components/ui';
import Collapsible from '../../components/record/Collapsible';
import FieldView from '../../components/record/FieldView';
import SiblingTimeline from '../../components/record/SiblingTimeline';
import { closedRecordLine, panelViewsFor } from '../../components/record/panels/registry';
import type { PanelSlot } from '../../components/record/panels/registry';
import type { VerbSaved } from '../../components/record/VerbForm';
import { useActionRuntime } from '../../hooks/useActionRuntime';
import { useActor } from '../../hooks/useActor';
import { DuplicatesContext, useDuplicates } from '../../hooks/useDuplicates';
import type { DuplicatesResult } from '../../hooks/useDuplicates';
import { useClosureTrail } from '../../hooks/useClosureTrail';
import { useRecord } from '../../hooks/useRecord';
import { isOversight } from '../../lib/actionAuthority';
import { matchWords } from '../../lib/duplicates';
import { CLOSURE_LABELS } from '../../lib/recordClosure';
import { missingLodgeColumns } from '../../lib/reportCompleteness';
import { isMyStream, needsClientId } from '../../lib/recordAuthority';
import { VERB_LABELS } from '../../lib/recordVerbs';
import { isLiveRef, isPracticeRef, parsePracticeRef, parseRef } from '../../lib/recordQuery';
import { RECORD_NOUNS, fieldSections, lodgedOn } from '../../lib/recordView';
import type { SectionId } from '../../lib/recordView';
import { isClosedStatus, nextStepPanel, openByDefault, panelOfHash } from '../../lib/recordSections';
import type { OpenByDefault } from '../../lib/recordSections';
import { isAuditRef } from '../../lib/auditQuery';
import AuditRecordView from './AuditRecordView';
import { REGISTERS } from '../../config/registers';
import { recordEditFormUrl } from '../../services/lodgeService';
import type { RecordIO } from '../../services/recordService';
import type { Actor } from '../../types/actionRegister';
import type { RecordRow } from '../../types/records';
import cardStyles from '../../components/record/record.module.css';
import styles from './RecordPage.module.css';

type RefKind = 'live' | 'practice' | 'bad';

function kindOf(ref: string | undefined): RefKind {
  if (!ref) return 'bad';
  if (isPracticeRef(ref)) return 'practice';
  return isLiveRef(ref) ? 'live' : 'bad';
}

function statusTone(status: string): 'success' | 'warning' | 'info' | 'neutral' {
  switch (status) {
    case 'Completed':
      return 'success';
    case 'Actioning':
    case 'Evaluating':
      return 'info';
    case 'Cancelled':
      return 'neutral';
    default:
      return 'warning';
  }
}

const VERB_WORDS: Readonly<Record<string, string>> = { ...VERB_LABELS, ...CLOSURE_LABELS };

/** A calm dead end: the reason, and a way on. */
function Elsewhere({ title, children }: { title: string; children: ReactNode }) {
  return (
    <PageContainer maxWidth="md">
      <h1 className={styles.title}>{title}</h1>
      <div className={styles.lede}>{children}</div>
      <p className={styles.lede}>
        <Link to="/lookup">Find it from Lookup</Link> or open it from a register table.
      </p>
    </PageContainer>
  );
}

/**
 * 13-32: `/record/AUD-<id>` (and a practice `AUD-P<n>`) is an Internal Audit, Oversight's list rather than one of the
 * five registers, so the page resolves the `AUD` prefix to the audit record view.
 */
export default function RecordPage() {
  const ref = useParams().ref ?? '';
  return isAuditRef(ref) ? <AuditRecordView /> : <RegisterRecordPage />;
}

function RegisterRecordPage() {
  const params = useParams();
  const raw = params.ref ?? '';
  const { mode, writer, io } = useActionRuntime();
  const actorState = useActor();
  const actor = actorState.actor;

  const kind = kindOf(raw);
  const practiceOk = kind === 'practice' && mode === 'practice';
  const canonical = kind === 'live' ? parseRef(raw).ref : kind === 'practice' ? parsePracticeRef(raw).ref : null;
  const readRef = kind === 'live' || practiceOk ? canonical : null;
  const record = useRecord(readRef);
  const trail = useClosureTrail(kind === 'live' ? canonical : null);

  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [owed, setOwed] = useState<{ ref: string; count: number; note: string | null } | null>(null);
  const [retryTick, setRetryTick] = useState(0);

  // Owed audit rows for this record (D-13-L-5): retried through the Writer, never posted from here directly.
  useEffect(() => {
    if (kind !== 'live' || !canonical || !io || !actor) return;
    let cancelled = false;
    writer
      .retryRecordAudit(io as RecordIO, actor, { ref: canonical })
      .then((r) => {
        if (cancelled) return;
        const count = r.stillOwed.filter((e) => e.ref === canonical).reduce((n, e) => n + e.drafts.length, 0);
        setOwed({ ref: canonical, count, note: r.note });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [kind, canonical, io, actor, writer, retryTick]);

  const reload = record.reload;
  const onSaved = useCallback(
    (s: VerbSaved) => {
      const words = VERB_WORDS[s.verb] ?? 'The change';
      // 13.2-10: rows owed on another record (the kept record's Note) are said too, never dropped.
      const other = s.otherPending
        ? ` ${s.otherPending.count} ${s.otherPending.count === 1 ? 'row' : 'rows'} for ${s.otherPending.ref} will be posted.`
        : '';
      setSavedNote(
        (s.auditPending > 0
          ? `${words} saved; ${s.auditPending} audit ${s.auditPending === 1 ? 'row' : 'rows'} will be posted.`
          : s.mode === 'practice'
            ? `${words} saved in practice mode. Nothing was written to SharePoint.`
            : `${words} saved.`) + other,
      );
      reload();
      setRetryTick((n) => n + 1);
    },
    [reload],
  );

  if (kind === 'bad') {
    return (
      <Elsewhere title="That reference isn't a register record">
        <p>
          <strong>{raw || 'This link'}</strong> doesn't look like a reference on one of the five registers (for example
          INC-12, CMP-3, FB-8, RSK-4 or CI-21), or an internal audit (AUD-3).
        </p>
      </Elsewhere>
    );
  }
  if (kind === 'practice' && !practiceOk) {
    return (
      <Elsewhere title="That's a practice record">
        <p>
          <strong>{raw}</strong> was made in practice mode. Practice records stay in the browser they were made in, and
          open only while practice mode is on.
        </p>
      </Elsewhere>
    );
  }
  if (kind === 'live' && canonical && canonical !== raw) {
    return <Navigate to={`/record/${canonical}`} replace />;
  }

  if (record.status === 'loading' || (record.status === 'ready' && !actor)) {
    return (
      <PageContainer>
        <div className={styles.loading} role="status" aria-live="polite">
          <Spinner size="lg" />
          <span>Opening {canonical}…</span>
        </div>
      </PageContainer>
    );
  }
  if (record.status === 'not-found') {
    return (
      <Elsewhere title={`${canonical} isn't on the register`}>
        <p>There's no record with that reference. It may have been deleted, or the number may be mistyped.</p>
      </Elsewhere>
    );
  }
  if (record.status !== 'ready' || !record.row || !actor) {
    return (
      <PageContainer maxWidth="md">
        <h1 className={styles.title}>{canonical}</h1>
        <p className={styles.failure} role="alert">
          Couldn't read this record from the Quality &amp; Safety site{record.error ? `: ${record.error}` : '.'}
        </p>
        <Button variant="secondary" onClick={record.reload}>
          Try again
        </Button>
      </PageContainer>
    );
  }

  return (
    <RecordView
      row={record.row}
      siblings={record.siblings}
      preparedByUpn={record.preparedByUpn}
      actor={actor}
      practice={kind === 'practice'}
      refreshing={record.refreshing}
      savedNote={savedNote}
      owed={owed && owed.ref === record.row.ref ? owed : null}
      onRetryOwed={() => setRetryTick((n) => n + 1)}
      onSaved={onSaved}
      trail={kind === 'live' ? trail : null}
    />
  );
}

interface RecordViewProps {
  row: RecordRow;
  siblings: ReturnType<typeof useRecord>['siblings'];
  preparedByUpn: string | undefined;
  actor: Actor;
  practice: boolean;
  refreshing: boolean;
  savedNote: string | null;
  owed: { count: number; note: string | null } | null;
  onRetryOwed: () => void;
  onSaved: (s: VerbSaved) => void;
  trail: ReturnType<typeof useClosureTrail> | null;
}

/** The open state's keys: `panel:<key>`, `section:<id>`, `area:<trail|links>`. */
const panelKey = (key: string) => `panel:${key}`;
const sectionKey = (id: SectionId) => `section:${id}`;
const areaKey = (id: 'trail' | 'links') => `area:${id}`;

function keysOf(o: OpenByDefault): Set<string> {
  return new Set([...[...o.panels].map(panelKey), ...[...o.sections].map(sectionKey), ...[...o.areas].map(areaKey)]);
}

/** The open state for a visit: the open keys, and the panel slots already seen (13.2-10, ISS-286). */
interface OpenState {
  ref: string;
  keys: Set<string>;
  /** Every panel slot key shown so far on this visit; a key not in it is newly visible. */
  seen: Set<string>;
}

function RecordView({ row, siblings, preparedByUpn, actor, practice, refreshing, savedNote, owed, onRetryOwed, onSaved, trail }: RecordViewProps) {
  const register = row.register;
  const f = REGISTERS[register].fields;
  const status = String(row.fields[f.status] ?? '');
  const unit = typeof row.fields[f.businessUnit] === 'string' ? (row.fields[f.businessUnit] as string) : '';
  const title = typeof row.fields[f.title] === 'string' && row.fields[f.title] ? (row.fields[f.title] as string) : row.ref;
  const lodged = lodgedOn(row, siblings[0]?.entryTimestamp ?? null);
  const editUrl = practice ? null : recordEditFormUrl(register, row.ref);
  const views = useMemo(() => panelViewsFor(row, actor, { preparedByUpn }), [row, actor, preparedByUpn]);
  const sections = useMemo(() => fieldSections(row), [row]);
  // D-13-33: a closed record shows no triage or handling; say so to whoever would otherwise have them.
  const closedLine = useMemo(() => closedRecordLine(row, actor, { preparedByUpn }), [row, actor, preparedByUpn]);
  // The open Closure Links, named in a closing step's confirm (D-13-L-14; 13-26). A practice record has none.
  const consequences = trail?.data?.consequences;
  const openLinks = useMemo(() => (consequences ?? []).filter((l) => l.linkStatus === 'Open').map((l) => l.targetRef), [consequences]);

  // 13.2-11: what is open. Initialised from `openByDefault` for this ref, then the person's own for the visit.
  const { hash } = useLocation();
  const reducedMotion = useReducedMotion() ?? false;
  const start = (): OpenState => ({
    ref: row.ref,
    keys: keysOf(openByDefault(views, sections, row, hash)),
    seen: new Set(views.map((v) => v.slot.key)),
  });
  const [open, setOpen] = useState<OpenState>(start);
  if (open.ref !== row.ref) {
    // A different record (not an eTag refresh of this one): start again from its defaults.
    setOpen(start());
  } else if (views.some((v) => !open.seen.has(v.slot.key))) {
    // 13.2-10 (ISS-286): a panel that became visible after a save opens once, when it is this person's to act on.
    const fresh = views.filter((v) => !open.seen.has(v.slot.key));
    const act = fresh.filter((v) => !v.readOnly && v.slot.component).map((v) => panelKey(v.slot.key));
    setOpen({ ...open, keys: new Set([...open.keys, ...act]), seen: new Set([...open.seen, ...fresh.map((v) => v.slot.key)]) });
  }
  const isOpen = (key: string) => open.keys.has(key);
  const toggle = (key: string) =>
    setOpen((prev) => {
      const keys = new Set(prev.keys);
      if (keys.has(key)) keys.delete(key);
      else keys.add(key);
      return { ...prev, keys };
    });
  const setAll = (keys: readonly string[], on: boolean) =>
    setOpen((prev) => {
      const next = new Set(prev.keys);
      for (const key of keys) {
        if (on) next.add(key);
        else next.delete(key);
      }
      return { ...prev, keys: next };
    });

  // A `#panel-<key>` link (Today, Verify, a chip, or a link inside a panel) opens that panel and brings it into view.
  const openFromHash = useCallback(
    (h: string, smooth: boolean) => {
      const key = panelOfHash(h);
      if (!key || !views.some((v) => v.slot.key === key)) return;
      setOpen((prev) => (prev.keys.has(panelKey(key)) ? prev : { ...prev, keys: new Set([...prev.keys, panelKey(key)]) }));
      // On arrival the page is still settling (panels read their lists), which cancels a smooth scroll: jump instead.
      requestAnimationFrame(() =>
        document.getElementById(`panel-${key}`)?.scrollIntoView({ behavior: smooth && !reducedMotion ? 'smooth' : 'auto', block: 'start' }),
      );
    },
    [views, reducedMotion],
  );
  useEffect(() => {
    if (hash) openFromHash(hash, false);
    // Only when the hash changes (or the record does): not on every eTag refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, row.ref]);
  useEffect(() => {
    const onHash = () => openFromHash(window.location.hash, true);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [openFromHash]);

  const panelKeys = views.map((v) => panelKey(v.slot.key));
  const nextKey = nextStepPanel(views, row)?.slot.key ?? null;
  const sectionKeys = sections.map((s) => sectionKey(s.id));
  const openSections = useMemo(
    () => new Set(sections.filter((s) => open.keys.has(sectionKey(s.id))).map((s) => s.id)),
    [sections, open.keys],
  );
  // 13.2-10: the head's notes. The report gaps on an open incident; the duplicate link; Oversight's possible match.
  const duplicates = useDuplicates(row, actor);
  const missing = useMemo(() => (isClosedStatus(row) ? [] : missingLodgeColumns(row)), [row]);
  const canComplete = views.some((v) => v.slot.key === 'complete' && !v.readOnly);
  const duplicateOf = typeof row.fields.DuplicateOfRef === 'string' ? row.fields.DuplicateOfRef.trim() : '';

  const links = trail?.data?.consequences;
  const linksHint = !trail ? 'None on a practice record' : links ? (links.length === 0 ? 'None yet' : `${links.length} ${links.length === 1 ? 'link' : 'links'}`) : null;

  return (
    <DuplicatesContext.Provider value={duplicates}>
    <PageContainer>
      <header className={styles.head}>
        <p className={styles.kicker}>
          {RECORD_NOUNS[register]} · <span className={styles.ref}>{row.ref}</span>
          {practice && <span className={styles.practiceTag}>Practice record</span>}
        </p>
        <h1 className={styles.title}>{title}</h1>
        <div className={styles.chips}>
          {status && (
            <Badge variant={statusTone(status)} size="sm">
              {status}
            </Badge>
          )}
          {unit && <span className={styles.chip}>{unit}</span>}
          {lodged && <span className={styles.chip}>Lodged {lodged}</span>}
          {isMyStream(actor, row) && <span className={`${styles.chip} ${styles.chipMine}`}>Your stream</span>}
          {needsClientId(row) && <span className={`${styles.chip} ${styles.chipTodo}`}>Client id to add</span>}
          {missing.length > 0 &&
            (canComplete ? (
              <a className={`${styles.chipLink} ${styles.chipTodo}`} href="#panel-complete">
                Report incomplete: {missing.length} to add
              </a>
            ) : (
              <span className={`${styles.chip} ${styles.chipTodo}`}>Report incomplete: {missing.length} to add</span>
            ))}
          {duplicateOf && (
            <Link className={styles.chipLink} to={`/record/${duplicateOf}`}>
              Duplicate of {duplicateOf}
            </Link>
          )}
          {editUrl ? (
            <a className={styles.chipLink} href={editUrl} target="_blank" rel="noreferrer">
              Attachments: open on SharePoint ↗
            </a>
          ) : (
            <span className={styles.chip}>Attachments: none on a practice record</span>
          )}
          {refreshing && <span className={styles.refreshing}>Refreshing…</span>}
        </div>
        {isOversight(actor) && <PossibleDuplicates duplicates={duplicates} />}
        {savedNote && (
          <p className={styles.savedNote} role="status">
            {savedNote}
          </p>
        )}
        {owed && owed.count > 0 && (
          <p className={styles.owed} role="status">
            {owed.count} audit {owed.count === 1 ? 'row' : 'rows'} for this record still to be posted.{' '}
            {owed.note ? `${owed.note} ` : ''}
            <button type="button" className={styles.textButton} onClick={onRetryOwed}>
              Post them now
            </button>
          </p>
        )}
      </header>

      <div className={styles.layout}>
        <section className={styles.act} aria-labelledby="record-act">
          <div className={styles.areaHead}>
            <h2 id="record-act" className={styles.areaTitle}>
              Work this record
            </h2>
            {views.length > 1 && <AllToggle what="panels" keys={panelKeys} isOpen={isOpen} setAll={setAll} />}
          </div>
          {closedLine && (
            <p className={styles.muted} role="note">
              {closedLine}
            </p>
          )}
          {!closedLine && views.every((v) => v.readOnly) && (
            <p className={styles.muted}>Nothing on this record is yours to record. You can read it, and its trail.</p>
          )}
          {views.length > 0 && (
            <div className={cardStyles.panels}>
              {views.map(({ slot, readOnly }) => (
                <Panel
                  key={slot.key}
                  slot={slot}
                  readOnly={readOnly}
                  next={slot.key === nextKey}
                  open={isOpen(panelKey(slot.key))}
                  onToggle={() => toggle(panelKey(slot.key))}
                  row={row}
                  siblings={siblings}
                  actor={actor}
                  openLinks={openLinks}
                  onSaved={onSaved}
                />
              ))}
            </div>
          )}
        </section>

        <section className={styles.read} aria-labelledby="record-read">
          <div className={styles.areaHead}>
            <h2 id="record-read" className={styles.areaTitle}>
              The record
            </h2>
            {sections.length > 1 && <AllToggle what="sections" keys={sectionKeys} isOpen={isOpen} setAll={setAll} />}
          </div>
          <FieldView row={row} sections={sections} open={openSections} onToggle={(id) => toggle(sectionKey(id))} />
        </section>

        <section className={styles.trail}>
          <Collapsible
            level={2}
            titleClassName={styles.areaTitle}
            title="What happened, newest first"
            open={isOpen(areaKey('trail'))}
            onToggle={() => toggle(areaKey('trail'))}
            hint={`${siblings.length} ${siblings.length === 1 ? 'entry' : 'entries'}`}
          >
            <div className={cardStyles.section}>
              <SiblingTimeline register={register} rows={siblings} actor={actor} />
            </div>
          </Collapsible>
        </section>

        <section className={styles.closure}>
          <Collapsible
            level={2}
            titleClassName={styles.areaTitle}
            title="Where it ended up"
            open={isOpen(areaKey('links'))}
            onToggle={() => toggle(areaKey('links'))}
            hint={linksHint}
          >
            <div className={cardStyles.section}>
              <ClosureLinks trail={trail} />
            </div>
          </Collapsible>
        </section>
      </div>
    </PageContainer>
    </DuplicatesContext.Provider>
  );
}

/**
 * 13.2-10 (D-13.2-P-10): Oversight's possible-duplicate note, under the head. A signal only: it names the record(s)
 * and why ("same client, 2 days apart"), and points to the closure step; it never closes anything. A failed read
 * says so in one quiet line, never as "no duplicates".
 */
function PossibleDuplicates({ duplicates }: { duplicates: DuplicatesResult }) {
  if (duplicates.status === 'error') {
    return (
      <p className={styles.muted} role="note">
        Couldn&apos;t check for a possible duplicate{duplicates.error ? `: ${duplicates.error}` : '.'}
      </p>
    );
  }
  if (duplicates.status !== 'ready' || duplicates.matches.length === 0) return null;
  return (
    <div className={styles.dupNote} role="note" aria-label="Possible duplicate">
      {duplicates.matches.map((m) => (
        <p key={m.ref} className={styles.dupLine}>
          <strong>Possible duplicate of </strong>
          <Link to={`/record/${m.ref}`}>{m.ref}</Link>
          {duplicates.titles[m.ref] ? <span className={styles.dupTitle}> ({duplicates.titles[m.ref]})</span> : null}: {matchWords(m)}.
        </p>
      ))}
      <p className={styles.dupHint}>
        A signal only: check both records. If this is the repeat, use <a href="#panel-closure">Close as a duplicate</a> here.
      </p>
    </div>
  );
}

/** "Expand all" and "Collapse all" for one area: text buttons beside its heading. */
function AllToggle({
  what,
  keys,
  isOpen,
  setAll,
}: {
  what: 'panels' | 'sections';
  keys: readonly string[];
  isOpen: (key: string) => boolean;
  setAll: (keys: readonly string[], on: boolean) => void;
}) {
  const allOpen = keys.every(isOpen);
  const allShut = !keys.some(isOpen);
  return (
    <div className={styles.allToggle}>
      <button type="button" className={styles.allButton} aria-label={`Expand all ${what}`} disabled={allOpen} onClick={() => setAll(keys, true)}>
        Expand all
      </button>
      <button type="button" className={styles.allButton} aria-label={`Collapse all ${what}`} disabled={allShut} onClick={() => setAll(keys, false)}>
        Collapse all
      </button>
    </div>
  );
}

function Panel({
  slot,
  readOnly,
  next,
  open,
  onToggle,
  row,
  siblings,
  actor,
  openLinks,
  onSaved,
}: {
  slot: PanelSlot;
  readOnly: boolean;
  /** D-13.2-A-9: this person's next step on the record (`nextStepPanel`), tagged so a collapsed page still says where to start. */
  next: boolean;
  open: boolean;
  onToggle: () => void;
  row: RecordRow;
  siblings: RecordViewProps['siblings'];
  actor: Actor;
  openLinks: readonly string[];
  onSaved: (s: VerbSaved) => void;
}) {
  const Component = slot.component;
  if (!Component) {
    return (
      <Collapsible
        id={`panel-${slot.key}`}
        className={`${cardStyles.panel} ${cardStyles.panelLater}`}
        titleClassName={cardStyles.panelTitle}
        title={slot.title}
        open={open}
        onToggle={onToggle}
        disabled
        tag={<span className={cardStyles.laterTag}>Arrives in the next update</span>}
      >
        <p className={cardStyles.later}>This step can't be recorded on the dashboard yet.</p>
      </Collapsible>
    );
  }
  return (
    <Collapsible
      id={`panel-${slot.key}`}
      className={cardStyles.panel}
      titleClassName={cardStyles.panelTitle}
      title={slot.title}
      open={open}
      onToggle={onToggle}
      tag={
        readOnly ? (
          <span className={cardStyles.laterTag}>Read only</span>
        ) : next ? (
          <span className={`${cardStyles.laterTag} ${cardStyles.nextTag}`}>Your next step</span>
        ) : undefined
      }
    >
      {/* Keyed by the eTag: a saved or re-read record starts the form from what is on it now. */}
      <Component
        key={`${row.ref}|${row.eTag}`}
        row={row}
        siblings={siblings}
        actor={actor}
        openLinks={openLinks}
        readOnly={readOnly}
        onSaved={onSaved}
      />
    </Collapsible>
  );
}

/** The record's Closure Links (D-13-L-14): each consequence, its status, and the open ones flagged. */
function ClosureLinks({ trail }: { trail: ReturnType<typeof useClosureTrail> | null }) {
  if (!trail) return <p className={styles.muted}>A practice record has no closure links.</p>;
  if (trail.isLoading) {
    return (
      <p className={styles.muted} role="status">
        Reading the closure links…
      </p>
    );
  }
  if (trail.error) {
    return (
      <p className={styles.failure} role="alert">
        Couldn't read the closure links: {trail.error}
      </p>
    );
  }
  const links = trail.data?.consequences ?? [];
  if (links.length === 0) {
    return <p className={styles.muted}>No closure links yet. Improvements, risks and care-plan revisions it leads to will show here.</p>;
  }
  const open = links.filter((l) => l.linkStatus === 'Open').length;
  return (
    <>
      <p className={styles.muted}>
        {open > 0 ? `${open} open ${open === 1 ? 'link' : 'links'}: closing doesn't wait for them, but the confirm names them.` : 'Every link is complete or cancelled.'}
        {trail.data?.readyToClose ? ' Ready to close.' : ''}
      </p>
      <ul className={styles.links}>
        {links.map((l) => (
          <li key={l.linkRef} className={styles.link}>
            <span className={styles.linkRel}>{l.relationship || 'linked'} →</span>{' '}
            {isLiveRef(l.targetRef) ? <Link to={`/record/${parseRef(l.targetRef).ref}`}>{l.targetRef}</Link> : <span>{l.targetRef}</span>}
            {l.targetTitle && <span className={styles.linkTitle}> {l.targetTitle}</span>}
            {l.targetStatus && <span className={styles.muted}> · {l.targetStatus}</span>}{' '}
            <Badge variant={l.linkStatus === 'Open' ? 'warning' : l.linkStatus === 'Complete' ? 'success' : 'neutral'} size="sm">
              {l.linkStatus === 'Open' ? 'Open link' : l.linkStatus}
            </Badge>
          </li>
        ))}
      </ul>
    </>
  );
}
